// Shop, part two on the web: discovery rails, wishlist heart, verified-buyer reviews and the Saved page.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Heart, People, Sparkles, Star, Tag, Wallet } from "./shopIcons";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/site";
import { api } from "@/lib/api";
import { gbp2 } from "@/lib/shopApi";
import { CreatorAvatar } from "@/components/stage/PostCard";

export const shopExtraApi = {
  home: () => api("/shop/home"),
  toggleSave: (id) => api("/shop/products/" + id + "/save", { method: "POST" }),
  saved: () => api("/shop/saved"),
  savedIds: () => api("/shop/saved/ids"),
  reviews: (id) => api("/shop/products/" + id + "/reviews"),
  review: (id, body) => api("/shop/products/" + id + "/reviews", { method: "POST", body }),
};

const ICONS = { flame: Flame, sparkles: Sparkles, people: People, pricetag: Tag, wallet: Wallet, heart: Heart };

function MiniCard({ p }) {
  return (
    <Link to={"/shop/" + p.id} className="group block w-40 shrink-0" data-testid={"rail-product-" + p.id}>
      <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-muted">
        {p.cover && <img src={p.cover} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
        {p.discount_pct ? <span className="absolute left-2 top-2 rounded-full bg-rose-600 px-2 py-0.5 text-[11px] font-bold text-white">-{p.discount_pct}%</span> : null}
      </div>
      <p className="mt-2 truncate text-sm font-semibold">{p.title}</p>
      <p className="flex items-center gap-1.5 text-sm font-bold">{gbp2(p.price_gbp)}{p.rating?.count ? <span className="inline-flex items-center gap-0.5 text-xs font-medium text-muted-foreground"><Star size={11} className="fill-amber-400 text-amber-400" />{p.rating.avg}</span> : null}</p>
    </Link>
  );
}

export function ShopRails() {
  const [sections, setSections] = useState(null);
  useEffect(() => { shopExtraApi.home().then((d) => setSections(d.sections || [])).catch(() => setSections([])); }, []);
  if (sections === null) return <Skeleton className="mt-6 h-64 rounded-2xl" />;
  if (!sections.length) return null;
  return (
    <div className="mt-6 space-y-7" data-testid="shop-rails">
      {sections.map((s) => {
        const Icon = ICONS[s.icon] || Sparkles;
        return (
          <section key={s.key} data-testid={"shop-rail-" + s.key}>
            <h2 className="flex items-center gap-2 text-lg font-bold"><Icon size={17} className="text-[#E0007A]" /> {s.title}</h2>
            <div className="mt-3 flex gap-4 overflow-x-auto pb-2 [scrollbar-width:thin]">{s.items.map((p) => <MiniCard key={p.id} p={p} />)}</div>
          </section>
        );
      })}
      <h2 className="text-lg font-bold">Everything</h2>
    </div>
  );
}

export function SaveButton({ id, className = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (user) shopExtraApi.savedIds().then((d) => setSaved((d.ids || []).includes(id))).catch(() => null); }, [id, user]);
  const toggle = async () => {
    if (!user) { navigate("/login", { state: { from: "/shop/" + id } }); return; }
    setSaved((v) => !v);
    try { const r = await shopExtraApi.toggleSave(id); setSaved(r.saved); toast(r.saved ? "Saved to your wishlist" : "Removed from saved"); } catch { setSaved((v) => !v); }
  };
  return (
    <button type="button" onClick={toggle} aria-pressed={saved} aria-label={saved ? "Remove from saved" : "Save"} data-testid="product-save" className={"inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card transition-colors hover:bg-muted " + className}>
      <Heart size={18} className={saved ? "fill-rose-600 text-rose-600" : ""} />
    </button>
  );
}

function Stars({ value, size = 14, onPick }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onPick} onClick={() => onPick && onPick(n)} aria-label={onPick ? n + " stars" : undefined} className={onPick ? "p-0.5" : "pointer-events-none"}>
          <Star size={size} className={value >= n - 0.25 ? "fill-amber-400 text-amber-400" : "text-muted-foreground/50"} />
        </button>
      ))}
    </span>
  );
}

export function ProductReviews({ id, mine }) {
  const [r, setR] = useState(null);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => shopExtraApi.reviews(id).then((d) => { setR(d); if (d.mine) { setRating(d.mine.rating); setText(d.mine.text); } }).catch(() => setR(null)), [id]);
  useEffect(() => { load(); }, [load]);
  const send = async (e) => {
    e.preventDefault();
    if (!rating) return;
    setBusy(true);
    try { await shopExtraApi.review(id, { rating, text: text.trim() }); toast.success("Thanks for your review"); load(); } catch (err) { toast.error(err.message || "Couldn't post your review."); } finally { setBusy(false); }
  };
  if (!r) return null;
  return (
    <section className="mt-10" data-testid="product-reviews">
      <h2 className="font-serif-display text-2xl font-bold">Reviews</h2>
      {r.count > 0 ? (
        <div className="mt-4 flex max-w-lg items-center gap-6 rounded-2xl border border-border bg-card p-4">
          <div className="text-center"><p className="text-4xl font-extrabold">{r.avg}</p><Stars value={r.avg} size={13} /><p className="mt-1 text-xs text-muted-foreground">{r.count} review{r.count === 1 ? "" : "s"}</p></div>
          <div className="flex-1 space-y-1">{[5, 4, 3, 2, 1].map((n) => (
            <div key={n} className="flex items-center gap-2 text-xs"><span className="w-3 text-muted-foreground">{n}</span><div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-amber-400" style={{ width: Math.round(((r.dist[String(n)] || 0) / r.count) * 100) + "%" }} /></div></div>
          ))}</div>
        </div>
      ) : <p className="mt-2 text-sm text-muted-foreground">No reviews yet. Only verified buyers can review.</p>}
      {r.can_review && !mine && (
        <form onSubmit={send} className="mt-4 max-w-lg rounded-2xl border border-border bg-card p-4" data-testid="review-form">
          <p className="text-sm font-semibold">{r.mine ? "Update your review" : "You bought this. How was it?"}</p>
          <div className="mt-2"><Stars value={rating} size={24} onPick={setRating} /></div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={1000} placeholder="Say what you liked (optional)" className="mt-3 w-full rounded-xl border border-border bg-background p-3 text-sm outline-none focus:border-primary" />
          <Button type="submit" disabled={!rating || busy} className="mt-3 rounded-full bg-[#E0007A] px-6 hover:bg-[#c4006a]">{r.mine ? "Update review" : "Post review"}</Button>
        </form>
      )}
      <ul className="mt-4 max-w-2xl divide-y divide-border">
        {r.items.map((it) => (
          <li key={it.id} className="py-4">
            <div className="flex items-center gap-2"><CreatorAvatar author={it.member || {}} size={28} /><span className="text-sm font-semibold">{it.member?.display_name || (it.member?.handle ? "@" + it.member.handle : "Buyer")}</span><span className="ml-auto"><Stars value={it.rating} size={12} /></span></div>
            <p className="mt-1 text-xs text-muted-foreground">Verified buyer · {String(it.created_at).slice(0, 10)}</p>
            {it.text && <p className="mt-2 text-sm leading-relaxed">{it.text}</p>}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function SavedProducts({ Card }) {
  usePageMeta("Saved", "Products you saved on Fable + Shop.");
  const [items, setItems] = useState(null);
  useEffect(() => { shopExtraApi.saved().then((d) => setItems(d.items || [])).catch(() => setItems([])); }, []);
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="shop-saved">
      <Link to="/shop" className="text-sm text-muted-foreground hover:text-foreground">← Shop</Link>
      <h1 className="mt-2 font-serif-display text-3xl font-bold">Saved</h1>
      {items === null ? <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />)}</div>
        : items.length === 0 ? <div className="mt-8 rounded-2xl border border-dashed border-border p-10 text-center"><Heart className="mx-auto text-muted-foreground" /><p className="mt-2 font-semibold">Nothing saved yet</p><p className="text-sm text-muted-foreground">Tap the heart on any product to keep it here.</p><Link to="/shop"><Button className="mt-4 rounded-full">Browse the Shop</Button></Link></div>
        : <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">{items.map((p) => <Card key={p.id} p={p} />)}</div>}
    </div>
  );
}
